import { useState, useEffect } from 'react';
import { ref, onValue, push, set } from 'firebase/database';
import { database } from '../config/firebase';
import { verifyPassword, isPasswordHashed, hashPassword } from '../utils/authUtils';

export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // データ読み込み
  useEffect(() => {
    const teachersRef = ref(database, 'teachers');
    const unsubscribeTeachers = onValue(teachersRef, async (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const teacherList = Object.entries(data).map(([key, value]) => ({
          id: key,
          ...value,
        }));
        setTeachers(teacherList);
      } else {
        setTeachers([]);
        // 初期のマスター教師を作成
        const hashedDefaultPassword = await hashPassword('password');
        const newTeacherRef = push(ref(database, 'teachers'));
        const newTeacher = {
          id: newTeacherRef.key,
          name: 'マスター教師',
          username: 'master',
          password: hashedDefaultPassword,
          isMaster: true,
        };
        await set(newTeacherRef, newTeacher);
        setTeachers([newTeacher]);
      }
      setIsDataLoaded(true);
    });

    const studentsRef = ref(database, 'students');
    const unsubscribeStudents = onValue(studentsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // orderプロパティを確実に設定
        const studentList = Object.entries(data).map(([id, student]) => ({
          ...student,
          id,
          order: student.order || 0 // orderが未設定の場合は0を設定
        }));
        // 順序でソート
        studentList.sort((a, b) => (a.order || 0) - (b.order || 0));
        setStudents(studentList);
      } else {
        setStudents([]);
      }
    });

    return () => {
      unsubscribeTeachers();
      unsubscribeStudents();
    };
  }, []);

  // ログイン処理
  const handleLogin = async (username, password) => {
    if (!isDataLoaded) {
      alert('データを読み込み中です。しばらくお待ちください。');
      return;
    }

    try {
      // 教師のログイン確認
      for (const teacher of teachers) {
        if (teacher.username === username) {
          let passwordMatch = false;
          
          // パスワードがハッシュ化されているかチェック
          if (isPasswordHashed(teacher.password)) {
            // ハッシュ化されたパスワードと比較
            passwordMatch = await verifyPassword(password, teacher.password);
          } else {
            // 従来の平文パスワードと比較（後方互換性）
            passwordMatch = teacher.password === password;
          }
          
          if (passwordMatch) {
            setCurrentUser({
              ...teacher,
              role: 'teacher',
              isMaster: teacher.isMaster || false,
            });
            return;
          }
        }
      }

      // 生徒のログイン確認
      for (const student of students) {
        if (student.username === username) {
          let passwordMatch = false;
          
          // パスワードがハッシュ化されているかチェック
          if (isPasswordHashed(student.password)) {
            // ハッシュ化されたパスワードと比較
            passwordMatch = await verifyPassword(password, student.password);
          } else {
            // 従来の平文パスワードと比較（後方互換性）
            passwordMatch = student.password === password;
          }
          
          if (passwordMatch) {
            setCurrentUser({ ...student, role: 'student' });
            return;
          }
        }
      }

      alert('ユーザー名またはパスワードが間違っています');
    } catch (error) {
      console.error('ログインエラー:', error);
      alert('ログイン中にエラーが発生しました。再度お試しください。');
    }
  };

  // ログアウト処理
  const handleLogout = () => {
    setCurrentUser(null);
  };

  return {
    currentUser,
    setCurrentUser,
    students,
    setStudents,
    teachers,
    setTeachers,
    isDataLoaded,
    handleLogin,
    handleLogout
  };
}; 